import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search } from "lucide-react";
import { api } from "../api";
import { useLang } from "../i18n";
import PolandMap from "../components/PolandMap";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { gminaPath } from "../slug";

const BANDS = ["gotowa", "w_trakcie", "ograniczona", "nie_wykryto", "zrodlo_niedostepne"];
const BAND_CHIP = {
  gotowa: "border-emerald-300 bg-emerald-50 text-emerald-800",
  w_trakcie: "border-amber-300 bg-amber-50 text-amber-800",
  ograniczona: "border-sky-300 bg-sky-50 text-sky-800",
  nie_wykryto: "border-slate-400 bg-slate-100 text-slate-700",
  zrodlo_niedostepne: "border-dashed border-slate-300 bg-white text-slate-500",
};

export default function IndexPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [gminas, setGminas] = useState(null);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [band, setBand] = useState("all");
  const [woj, setWoj] = useState("all");
  const [shown, setShown] = useState(200);

  useEffect(() => {
    api.post("/analytics/event", { event: "index_view" }).catch(() => {});
    api.get("/index/gminas", { params: { limit: 3000 } }).then((r) => setGminas(r.data.results)).catch(() => setGminas([]));
    api.get("/index/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const wojList = useMemo(
    () => [...new Set((gminas || []).map((g) => g.wojewodztwo).filter(Boolean))].sort(),
    [gminas]
  );
  const filtered = (gminas || []).filter((g) =>
    (band === "all" || g.band === band) &&
    (woj === "all" || g.wojewodztwo === woj) &&
    (!q.trim() || g.name.toLowerCase().includes(q.trim().toLowerCase())));
  const mapped = filtered.filter((g) => g.centroid).map((g) => ({
    ...g, lat: g.centroid.lat, lng: g.centroid.lon, plan_ogolny_status: g.band || "unknown",
  }));

  return (
    <div data-testid="index-page" className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <Helmet>
        <title>{`${t("index.title")} | PlanGmina`}</title>
        <meta name="description" content={t("index.subtitle")} />
      </Helmet>
      <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">{t("index.title")}</h1>
      <p className="mt-2 max-w-3xl text-base text-slate-600">{t("index.subtitle")}</p>

      {stats && (
        <div data-testid="index-stats" className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-6">
          <div className="rounded-xl border bg-slate-900 p-4 text-center text-white">
            <p className="font-heading text-3xl font-extrabold">{stats.total}</p>
            <p className="text-xs uppercase tracking-wide text-slate-400">{t("index.stats.total")}</p>
          </div>
          <div className="rounded-xl border bg-white p-4 text-center">
            <p className="font-heading text-3xl font-extrabold">{stats.harvested}</p>
            <p className="text-xs uppercase tracking-wide text-slate-400">{t("index.stats.harvested")}</p>
          </div>
          {BANDS.map((b) => (
            <div key={b} data-testid={`index-stat-${b}`} className="rounded-xl border bg-white p-4 text-center">
              <p className="font-heading text-3xl font-extrabold">{stats.by_band[b] || 0}</p>
              <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold ${BAND_CHIP[b]}`}>
                {t(`band.${b}`)}
              </span>
            </div>
          ))}
        </div>
      )}
      {stats?.last_update && (
        <p data-testid="index-last-update" className="mt-2 text-xs text-slate-400">
          {t("index.stats.lastUpdate")}: {stats.last_update.slice(0, 16).replace("T", " ")} UTC · {t("index.daily")}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="index-search-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); setShown(200); }}
            placeholder={t("index.search")}
            className="w-64 rounded-md border bg-white py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#DC143C]/40"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button
            data-testid="index-filter-band-all"
            onClick={() => { setBand("all"); setShown(200); }}
            className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${band === "all" ? "border-slate-900 bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          >
            {t("index.allBands")}
          </button>
          {BANDS.map((b) => (
            <button
              key={b}
              data-testid={`index-filter-band-${b}`}
              onClick={() => { setBand(b); setShown(200); }}
              className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${band === b ? BAND_CHIP[b] + " ring-2 ring-current/20" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              {t(`band.${b}`)}
            </button>
          ))}
        </div>
        <Select value={woj} onValueChange={(v) => { setWoj(v); setShown(200); }}>
          <SelectTrigger data-testid="index-filter-woj" className="w-56 bg-white">
            <SelectValue placeholder={t("index.allWoj")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("index.allWoj")}</SelectItem>
            {wojList.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <div className="rounded-xl border bg-white p-4 lg:col-span-3">
          <p className="mb-2 text-xs text-slate-400">{t("index.map.hint")}</p>
          <PolandMap
            gminas={mapped}
            labels={false}
            testId="index-map"
            onSelect={(g) => navigate(gminaPath(g))}
          />
        </div>

        <div className="rounded-xl border bg-white lg:col-span-2">
          <div className="max-h-[520px] divide-y overflow-y-auto">
            {gminas === null && <p className="p-4 text-sm text-slate-500">{t("common.loading")}</p>}
            {gminas !== null && filtered.length === 0 && (
              <p data-testid="index-no-results" className="p-4 text-sm text-slate-500">{t("index.noResults")}</p>
            )}
            {filtered.slice(0, shown).map((g) => (
              <button
                key={g.id}
                data-testid={`index-gmina-row-${g.id}`}
                onClick={() => navigate(gminaPath(g))}
                className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition-colors hover:bg-slate-50"
              >
                <div>
                  <p className="text-sm font-semibold">{g.name}</p>
                  <p className="text-xs text-slate-500">{g.powiat} · woj. {g.wojewodztwo}</p>
                </div>
                {g.band ? (
                  <span data-testid={`index-band-${g.id}`} className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold ${BAND_CHIP[g.band]}`}>
                    {t(`band.${g.band}`)}
                  </span>
                ) : (
                  <span className="shrink-0 text-[10px] font-semibold text-slate-400">{t("index.pending")}</span>
                )}
              </button>
            ))}
            {filtered.length > shown && (
              <button
                data-testid="index-more-button"
                onClick={() => setShown((s) => s + 200)}
                className="w-full px-4 py-3 text-center text-xs font-bold text-[#DC143C] hover:bg-slate-50"
              >
                {t("index.more")} ({filtered.length - shown})
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
